import { isEqual } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, debounce, fork, put, select, takeLeading, takeLatest } from 'redux-saga/effects';

import { getAllClientDatasetsByParentId, getDatasetDefinitionsByParentId, upsertHiddenFields } from '../../../../services/dataset';
import { DatasetDefinition, DocumentSpecificHiddenFields, HiddenFields } from '../../../datasets/store/types';
import { fetchAllClientDatasetsFailed, fetchAllClientDatasetsSuccessful, fetchClientDatasetDefinitionsFailed, fetchClientDatasetDefinitionsStarted, fetchClientDatasetDefinitionsSuccessful, hiddenFieldsHaveUpdated, saveHiddenFieldsFailed, saveHiddenFieldsSuccessful, setClientDatasetView } from './actions';
import { getCurrentHiddenFields, getSavedHiddenFields, getSelectedDatasetId } from './selectors';
import { ClientDatasetActionTypes, ClientDatasets, ClientDatasetView } from './types';

export function* attemptFetchAllClientDatasets() {
    try {
        const datasetDocumentNamesAndAgreementTypes: ClientDatasets[] = yield call(getAllClientDatasetsByParentId);
        yield put(fetchAllClientDatasetsSuccessful(datasetDocumentNamesAndAgreementTypes));
    } catch (e) {
        toast.error('Unable to fetch the dataset document names and types. Please try again.');
        yield put(fetchAllClientDatasetsFailed((e as Error).message));
    }
}

function* fetchAllClientDatasetsWatcher() {
    yield takeLeading(ClientDatasetActionTypes.FETCH_ALL_CLIENT_DATASETS_STARTED, attemptFetchAllClientDatasets);
}

export function* redirectClientDatasetView({ payload }: ReturnType<typeof setClientDatasetView>) {
    const { view, datasetId, redirect } = payload;
    if (view !== ClientDatasetView.LIST) {
        const pageView = view === ClientDatasetView.PREVIEW ? 'preview' : 'configure';
        const navigationPath = `/admin/datasets/${pageView}/${datasetId}`;
        if (redirect) {
            yield put(push(navigationPath));
        }
    } else {
        const navigationPath = '/admin/datasets';
        yield put(push(navigationPath));
    }
}

function* redirectClientDatasetViewWatcher() {
    yield takeLatest(ClientDatasetActionTypes.CLIENT_DATASET_VIEW, redirectClientDatasetView);
}

export function* attemptFetchClientDatasetDefinitions({ payload }: ReturnType<typeof fetchClientDatasetDefinitionsStarted>) {
    try {
        const { definitions, hiddenFields, documentHiddenFields, riskToleranceIsRecalculating }: { definitions: DatasetDefinition[]; hiddenFields: HiddenFields; documentHiddenFields: DocumentSpecificHiddenFields; riskToleranceIsRecalculating: boolean; riskToleranceIncludesOverrides: boolean; } = yield call(getDatasetDefinitionsByParentId, { datasetId: payload });
        yield put(fetchClientDatasetDefinitionsSuccessful(definitions, hiddenFields, documentHiddenFields, riskToleranceIsRecalculating));
    } catch (e) {
        toast.error('Unable to fetch dataset definitions. Please try again.');
        yield put(fetchClientDatasetDefinitionsFailed((e as Error).message));
    }
}

function* fetchClientDatasetDefinitionsWatcher() {
    yield takeLeading(ClientDatasetActionTypes.FETCH_CLIENT_DATASET_DEFINITIONS_STARTED, attemptFetchClientDatasetDefinitions);
}

export function* attemptUpsertHiddenFields() {
    try {
        const hiddenFields: HiddenFields = yield select(getCurrentHiddenFields);
        const parentDatasetId: number = yield select(getSelectedDatasetId);
        const { riskToleranceIsRecalculating }: { riskToleranceIsRecalculating: boolean; } = yield call(upsertHiddenFields, { hiddenFields, parentDatasetId });
        yield put(saveHiddenFieldsSuccessful(riskToleranceIsRecalculating));
        toast('Personalised dataset configuration updated successfully.');
    } catch (e) {
        toast.error('Unable to save your dataset configuration. Please try again.');
        yield put(saveHiddenFieldsFailed((e as Error).message));
    }
}

function* upsertHiddenFieldsWatcher() {
    yield takeLeading(ClientDatasetActionTypes.SAVE_HIDDEN_FIELDS_STARTED, attemptUpsertHiddenFields);
}

export function* checkHiddenFieldsUpdated() {
    try {
        const savedHiddenFields: HiddenFields = yield select(getSavedHiddenFields);
        const currentHiddenFields: HiddenFields = yield select(getCurrentHiddenFields);
        const hiddenFieldsUpdated = !isEqual(Object.values(currentHiddenFields), Object.values(savedHiddenFields));
        yield put(hiddenFieldsHaveUpdated(hiddenFieldsUpdated));
    } catch (e) {
        toast.error('We encountered a problem checking if you had made any changes.');
    }
}

function* checkHiddenFieldsUpdatedWatcher() {
    yield debounce(500, ClientDatasetActionTypes.TOGGLE_HIDDEN_FIELDS, checkHiddenFieldsUpdated);
}

export function* clientDatasetsSaga() {
    yield all([
        fork(fetchAllClientDatasetsWatcher),
        fork(redirectClientDatasetViewWatcher),
        fork(fetchClientDatasetDefinitionsWatcher),
        fork(upsertHiddenFieldsWatcher),
        fork(checkHiddenFieldsUpdatedWatcher)
    ]);
}
